/**
 * Local-only preferences (localStorage). Per-peer volume/mute are local by design (§6.2).
 * Every access is wrapped: storage can be unavailable (private mode, blocked site data).
 */
import type { MicMode } from './api/protocol';

export type ThemePref = 'system' | 'light' | 'dark';

export interface Prefs {
  micMode: MicMode;
  /** KeyboardEvent.code of the push-to-talk key. */
  pttKey: string;
  inputDeviceId: string | null;
  outputDeviceId: string | null;
  /** 0..1.5 */
  masterVolume: number;
  theme: ThemePref;
  headphonesHintDismissed: boolean;
  startMuted: boolean;
  /** Where player heads come from: a public skin service or the locally generated pixel head. */
  /** Voice activation: true = browser/LiveKit automatic (DTX), false = manual threshold gate. */
  autoSensitivity: boolean;
  /** Manual gate threshold on the mic level meter, 0..1. */
  vadThreshold: number;
  /** Modalità altoparlanti (casse): AGC spento, soglia minima, ducking, anti-fischio sensibile. */
  speakerMode: boolean;
  /** Ducking anche in cuffia (in modalità altoparlanti è sempre attivo). */
  duckInHeadphones: boolean;
  /** Quanto abbassare le voci mentre parli, 0..1. */
  duckAmount: number;
  /** Riduzione rumore avanzata (RNNoise). Disattiva di default: usa più CPU. */
  advancedNoiseSuppression: boolean;
  /** Uscita delle voci: stereo con direzione 3D (default) o mono. */
  audioOutput: 'stereo' | 'mono';
  /** Effetto prossimità: il volume cala con la distanza (default). Spento = volume pieno nel raggio. */
  proximityEffect: boolean;
  /** Info box "La Chat Vocale continua a funzionare anche se nascosta" chiusa con "Non mostrare più". */
  backgroundHintDismissed: boolean;
}

export interface PeerPref {
  volume: number;
  muted: boolean;
}

export const DEFAULT_PREFS: Prefs = {
  micMode: 'vad',
  pttKey: 'KeyV',
  inputDeviceId: null,
  outputDeviceId: null,
  masterVolume: 1,
  theme: 'dark',
  headphonesHintDismissed: false,
  startMuted: false,
  autoSensitivity: true,
  vadThreshold: 0.12,
  speakerMode: false,
  duckInHeadphones: true,
  duckAmount: 0.5,
  advancedNoiseSuppression: false,
  audioOutput: 'stereo',
  proximityEffect: true,
  backgroundHintDismissed: false,
};

const PREFS_KEY = 'vc:prefs';
const PEER_PREFIX = 'vc:peer:';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: keep in memory only */
  }
}

let cache: Prefs | null = null;
const listeners = new Set<(p: Prefs) => void>();

export function loadPrefs(): Prefs {
  if (cache) return cache;
  let parsed: Partial<Prefs> = {};
  const raw = read(PREFS_KEY);
  if (raw) {
    try {
      parsed = JSON.parse(raw) as Partial<Prefs>;
    } catch {
      parsed = {};
    }
  }
  // drop keys of removed features (e.g. the old "panelCollapsed")
  const known = Object.fromEntries(Object.entries(parsed).filter(([k]) => k in DEFAULT_PREFS)) as Partial<Prefs>;
  cache = { ...DEFAULT_PREFS, ...known };
  return cache;
}

export function savePrefs(patch: Partial<Prefs>): Prefs {
  cache = { ...loadPrefs(), ...patch };
  write(PREFS_KEY, JSON.stringify(cache));
  for (const l of listeners) l(cache);
  return cache;
}

export function subscribePrefs(l: (p: Prefs) => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function loadPeerPref(identity: string): PeerPref {
  const raw = read(PEER_PREFIX + identity);
  if (raw) {
    try {
      const p = JSON.parse(raw) as Partial<PeerPref>;
      return { volume: typeof p.volume === 'number' ? p.volume : 1, muted: p.muted === true };
    } catch {
      /* ignore */
    }
  }
  return { volume: 1, muted: false };
}

export function savePeerPref(identity: string, pref: PeerPref): void {
  if (pref.volume === 1 && !pref.muted) write(PEER_PREFIX + identity, null);
  else write(PEER_PREFIX + identity, JSON.stringify(pref));
}

/** Identities with a non-default saved preference. */
export function listPeerPrefs(): Array<{ identity: string; pref: PeerPref }> {
  const out: Array<{ identity: string; pref: PeerPref }> = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PEER_PREFIX)) {
        const identity = k.slice(PEER_PREFIX.length);
        out.push({ identity, pref: loadPeerPref(identity) });
      }
    }
  } catch {
    /* ignore */
  }
  return out;
}

export function clearPeerPrefs(): void {
  for (const { identity } of listPeerPrefs()) write(PEER_PREFIX + identity, null);
}

/** Readable name of a KeyboardEvent.code for the UI. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const map: Record<string, string> = {
    Space: 'Spazio',
    ShiftLeft: 'Maiusc sx',
    ShiftRight: 'Maiusc dx',
    ControlLeft: 'Ctrl sx',
    ControlRight: 'Ctrl dx',
    AltLeft: 'Alt',
    AltRight: 'Alt Gr',
    Backquote: '\\',
    CapsLock: 'Bloc Maiusc',
    Tab: 'Tab',
  };
  return map[code] ?? code;
}
